import React, { memo, useMemo, useState } from 'react';
import cn from 'classnames';

import { prepareDataIncluded } from '@utils/kpi-utils/prepareDataIncluded';

import { IChart } from '@interfaces/kpi/kpiCommon';

import { ETechnologiesLabels } from '@enums/common';

import { borderColors, colors } from '@constants/colors';
import { COUNT_OF_COLORS } from '@constants/KPI/kpi-common';

import styles from './SitesCellsDataIncluded.module.scss';

interface ISitesCellsDataIncludedProps {
	charts: Array<IChart>;
}

const FIRST_INDEX = 0;

const SitesCellsDataIncluded = ({ charts }: ISitesCellsDataIncludedProps) => {
	const [selectedKpi, setSelectedKpi] = useState(`${charts[FIRST_INDEX].name}${FIRST_INDEX}`);
	const dataIncluded = useMemo(() => charts.map(({ sites, name }) => {
		return ({
			data: prepareDataIncluded(sites),
			kpiName: name,
		});
	}), [charts]);
	const withTabs = charts.length > 1;

	const selectedDataIncluded = useMemo(() => {
		return dataIncluded.find(({ kpiName }, index) => `${kpiName}${index}` === selectedKpi);
	}, [dataIncluded, selectedKpi]);

	const onClickTab = (kpiName: string) => setSelectedKpi(kpiName);

	return (
		<div className={styles.sitesCellsDataIncludedContainer}>
			{withTabs && <div
				className={styles.tabs}
				style={{
					gridTemplateColumns: `repeat(${dataIncluded.length}, 1fr)`,
				}}
			>
				{dataIncluded.map(({ kpiName }, index) => {
					const colorIndex = index % COUNT_OF_COLORS;
					const kpiColor = borderColors[colorIndex] || colors.indigo50;
					const onClick = () => onClickTab(`${kpiName}${index}`);
					const selected = `${kpiName}${index}` === selectedKpi;

					return (
						<div
							key={kpiName}
							className={cn(styles.kpiName, { [styles.selected]: selected })}
							onClick={onClick}
						>
							<div>
								<span
									className={styles.dot}
									style={{ background: kpiColor }}
								/> {kpiName}
							</div>
							<div
								className={styles.separator}
							/>
						</div>
					);
				})
				}
			</div>
			}
			<div>
				{selectedDataIncluded?.data?.map(({ technology, sites }) => {
					const midpoint = Math.ceil(sites.length / 2);
					const preparedSites = [sites.slice(0, midpoint), sites.slice(midpoint)];

					return (
						<div
							key={technology}
							className={styles.technologyWrapper}
						>
							<div className={styles.technologyName}>
								{ETechnologiesLabels[technology]}
							</div>
							<div className={styles.sitesWrapper}>
								{preparedSites.map((sites, index) => {
									return (
										<div
											key={`${technology}${index}`}
											className={styles.col}
										>
											{sites.map(({ siteId, cellIds }) => {
												return (
													<div
														key={siteId}
														className={styles.site}
													>
														<div className={styles.sitesName}>
															{siteId}:
														</div>
														<div className={styles.cellIdsWrapper}>
															{cellIds?.map((id, index) => {
																const comma = cellIds.length - 1 !== index ? ',' : '';

																return (
																	<div key={id}>
																		{id}{comma}
																	</div>
																);
															})
															}
														</div>
													</div>
												);
											})
											}
										</div>
									);
								})
								}
							</div>
						</div>
					);
				})
				}
			</div>
		</div>
	);
};

export default memo(SitesCellsDataIncluded);
